/**
 * Recent-properties tracking helpers.
 *
 * Separated from the RecentlyAccessed component file so the file
 * stays component-only (vite-react/fast-refresh wants pure-component
 * modules to enable hot reload).
 *
 * Persistence model:
 *   - localStorage (per user id) for instant first-paint
 *   - Server POST /api/users/:userId/recent-properties as best-effort
 *     write — errors swallowed
 */

import { apiPost } from '@/api/client';

export interface RecentAccessedRow {
  buildingId: number;
  buildingName: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  lastViewedAt: string; // ISO
}

const MAX_LOCAL_RECENTS = 20;
const STORAGE_KEY = 'truspace.recent-properties';

function storageKey(userId: number | undefined): string {
  return `${STORAGE_KEY}.${userId ?? 'anon'}`;
}

export function loadRecentsCache(userId: number | undefined): RecentAccessedRow[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentAccessedRow[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRecentsCache(userId: number | undefined, rows: RecentAccessedRow[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(rows));
  } catch {
    /* quota / private mode — ignore */
  }
}

interface RecordArgs {
  userId: number | undefined;
  buildingId: number;
  buildingName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

const LOCATION_FIELDS = ['buildingName', 'address', 'city', 'state', 'zip'] as const;

function filledText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Keep the newer view, but copy any blank name/address parts from the other copy.
 * A localStorage row written before the street address was cached must not hide
 * the address the server already joined from the building (FR-343).
 */
function preferRow(newer: RecentAccessedRow, older: RecentAccessedRow): RecentAccessedRow {
  const next: RecentAccessedRow = { ...newer };
  for (const field of LOCATION_FIELDS) {
    if (!filledText(next[field])) {
      const fallback = filledText(older[field]);
      if (fallback) next[field] = fallback;
    }
  }
  return next;
}

/**
 * Record that the current user viewed a property. Updates the local
 * cache immediately and fires a best-effort POST to the server. Errors
 * are swallowed — this should never break a page load.
 */
export function recordPropertyView({
  userId,
  buildingId,
  buildingName,
  address,
  city,
  state,
  zip,
}: RecordArgs): void {
  if (!buildingId || !buildingName) return;
  const row: RecentAccessedRow = {
    buildingId,
    buildingName,
    address,
    city,
    state,
    zip,
    lastViewedAt: new Date().toISOString(),
  };

  // 1. Local cache (instant)
  const existing = loadRecentsCache(userId).filter((r) => r.buildingId !== buildingId);
  saveRecentsCache(userId, [row, ...existing].slice(0, MAX_LOCAL_RECENTS));

  // 2. Server (best-effort)
  if (userId) {
    apiPost(`/users/${userId}/recent-properties`, {
      buildingId,
      buildingName,
      address,
      city,
      state,
      zip,
    }).catch(() => { /* fire and forget */ });
  }
}

/**
 * Merge server + local lists, dedupe by buildingId keeping the most recent timestamp.
 *
 * DEF-153: the server list is now scope-filtered and authoritative. Once it has loaded
 * (`serverLoaded`), it defines which buildings the caller may see — a local-cache row is only kept
 * if the server also returned that building, so a stale/foreign entry left in this browser's
 * localStorage (e.g. after a portfolio change or a different account on the same machine) can never
 * surface an out-of-scope property. Before the server responds we fall back to the cache for an
 * instant first paint; any transient out-of-scope card is replaced the moment the server answers.
 */
export function mergeRecents(
  server: RecentAccessedRow[],
  local: RecentAccessedRow[],
  serverLoaded = true,
): RecentAccessedRow[] {
  const allowed = serverLoaded
    ? new Set(server.map((r) => r.buildingId))
    : null;
  const scopedLocal = allowed
    ? local.filter((r) => allowed.has(r.buildingId))
    : local;

  const byId = new Map<number, RecentAccessedRow>();
  for (const row of [...server, ...scopedLocal]) {
    const existing = byId.get(row.buildingId);
    if (!existing) {
      byId.set(row.buildingId, row);
    } else if (existing.lastViewedAt < row.lastViewedAt) {
      byId.set(row.buildingId, preferRow(row, existing));
    } else {
      byId.set(row.buildingId, preferRow(existing, row));
    }
  }
  return [...byId.values()].sort((a, b) =>
    a.lastViewedAt < b.lastViewedAt ? 1 : -1,
  );
}
